// Credits system for supply role (landlords/agents).
// All data stored in localStorage under a single namespaced key.
// When we plug in Stripe later, only `balance` and `history` move to server —
// `agentProWaitlistEmail` and `betaBannerDismissed` stay client-side.

const CREDITS_KEY = 'izio_credits_v1'

export type CreditSource =
  | 'beta'
  | 'purchase'
  | 'refund'
  | 'agent_pro'
  | 'consume'

export interface CreditEvent {
  ts: string
  delta: number
  reason: string
  source: CreditSource
}

export interface CreditsState {
  balance: number
  history: CreditEvent[]
  initialized: boolean
  agentProWaitlistEmail?: string
  betaBannerDismissed: boolean
}

const EMPTY_STATE: CreditsState = {
  balance: 0,
  history: [],
  initialized: false,
  betaBannerDismissed: false,
}

function read(): CreditsState {
  if (typeof window === 'undefined') return { ...EMPTY_STATE }
  try {
    const raw = localStorage.getItem(CREDITS_KEY)
    if (!raw) return { ...EMPTY_STATE }
    const parsed = JSON.parse(raw)
    return {
      ...EMPTY_STATE,
      ...parsed,
      history: Array.isArray(parsed.history) ? parsed.history : [],
    }
  } catch {
    return { ...EMPTY_STATE }
  }
}

function write(state: CreditsState): void {
  if (typeof window === 'undefined') return
  try {
    localStorage.setItem(CREDITS_KEY, JSON.stringify(state))
  } catch (e) {
    console.error('Failed to save credits state', e)
  }
}

export function getCreditsState(): CreditsState {
  return read()
}

export function getCredits(): number {
  return read().balance
}

export function getCreditsHistory(): CreditEvent[] {
  return read().history
}

/**
 * Initialize the user's credits with the Beta freebie. Idempotent — if the
 * user already has an initialized credits state we leave it alone.
 * Call this exactly once per supply-role signup (or on first session refresh
 * for an existing supply session that has no credits yet).
 */
export function initBetaCredits(amount: number): void {
  const current = read()
  if (current.initialized) return
  const ts = new Date().toISOString()
  write({
    ...current,
    balance: amount,
    initialized: true,
    history: [
      {
        ts,
        delta: amount,
        reason: 'Pachet Beta — credite gratuite',
        source: 'beta',
      },
      ...current.history,
    ],
  })
}

export function addCredits(
  amount: number,
  source: CreditSource,
  reason: string,
): number {
  if (amount <= 0) return read().balance
  const current = read()
  const next: CreditsState = {
    ...current,
    initialized: true,
    balance: current.balance + amount,
    history: [
      {
        ts: new Date().toISOString(),
        delta: amount,
        reason,
        source,
      },
      ...current.history,
    ],
  }
  write(next)
  return next.balance
}

/**
 * Consume 1 credit. Returns the new balance on success, or null if the user
 * had 0 credits (caller should show paywall in that case).
 */
export function consumeCredit(reason: string): number | null {
  const current = read()
  if (current.balance <= 0) return null
  const next: CreditsState = {
    ...current,
    balance: current.balance - 1,
    history: [
      {
        ts: new Date().toISOString(),
        delta: -1,
        reason,
        source: 'consume',
      },
      ...current.history,
    ],
  }
  write(next)
  return next.balance
}

// --- Beta banner dismissal ---

export function isBetaBannerDismissed(): boolean {
  return read().betaBannerDismissed
}

export function dismissBetaBanner(): void {
  const current = read()
  write({ ...current, betaBannerDismissed: true })
}

// --- Agent PRO waitlist ---

export function getAgentProWaitlistEmail(): string | undefined {
  return read().agentProWaitlistEmail
}

export function setAgentProWaitlistEmail(email: string): void {
  const current = read()
  write({ ...current, agentProWaitlistEmail: email })
}

// --- Reset (dev / demo personas) ---

export function resetCredits(): void {
  if (typeof window === 'undefined') return
  try {
    localStorage.removeItem(CREDITS_KEY)
  } catch {
    /* noop */
  }
}
